import { Alert, AlertContext, LocationIcon, ProfilePhoto } from "@minervaproject/fds";
import { useContext } from "react";

import { Device } from "./device";
import { UserComputer } from "./user_computer";
import { UserConnection } from "./user_connection";
import { UserStatus } from "./user_status";

import { LocalModal } from "components/local_modal";
import { MonitoringPubsubMessageObserver } from "components/observers/monitoring_pubsub_message_observer";
import { ENVIRONMENT } from "config";
import { useAppSelector } from "hooks/redux_hooks";
import { useUserProfileProperties } from "hooks/use_user_properties";
import { getClassStatus } from "shared/utils/monitoring";
import { classroomSelectors } from "store";
import { formatDateTime } from "utils/date";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

import "./monitoring.scss";

function MonitoringClassUserDashboard({ classId, userId }: { classId: string; userId: string }) {
  const { alerts, ref } = useContext(AlertContext);
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const currentlyPresentUserCount = useAppSelector(
    classroomSelectors.selectCurrentlyPresentUserCount(),
  );
  const user = useUserProfileProperties(userId);
  const intercomUrl = `https://app.intercom.com/apps/oky10orf/users/show?user_id=${ENVIRONMENT}-${userId}`;

  if (!classInfo || !user) return null;

  return (
    <div className="vertical-scroll" style={{ height: "100vh" }}>
      <div className="mx5 mt5">
        {alerts.map((alert) => (
          <Alert ref={ref} id={alert.id} key={alert.id} kind={alert.kind}>
            {alert.content}
          </Alert>
        ))}
      </div>
      <div className="body text-black-tint-20" style={{ padding: "40px 80px 60px" }}>
        <h1 className="h2">{classInfo.title || classId}</h1>
        <div className="flex">
          <div className="mr3">Organization: {classInfo.org.name}</div>
          <div className="mr3">Status: {getClassStatus(classInfo, currentlyPresentUserCount)}</div>
          <div className="mr3">
            Class ID{" "}
            <a target="_blank" rel="noreferrer" href={`/monitoring/classes/${classInfo.id}`}>
              {`#${classInfo.id}`}
            </a>
          </div>
          <div>Class Start: {formatDateTime(classInfo.date)}</div>
          {classInfo.type === "in-person" ? (
            <div className="flex items-center ml3">
              <LocationIcon className="mr1" width={16} height={16} />
              <span>In-Person Class</span>
            </div>
          ) : null}
        </div>

        <div
          className="flex items-center my5 pb5"
          style={{ borderBottom: "1px solid var(--black-tint-90)" }}
        >
          <ProfilePhoto
            className="mr2"
            src={profilePhotoUrl(user)}
            initials={computeInitials(user)}
          />
          <div>
            <div className="bold">{longDisplayName(user)}</div>
            <div className="flex body-s">
              <div className="mr3">
                {`User ID #${userId}`}
                {/* User ID <UserLink userId={userId} text={`#${userId}`} /> */}
              </div>
              <div className="mr3">
                <a href={`mailto:${user.email}`} target="_blank" rel="noreferrer">
                  {user.email}
                </a>
              </div>
              <a href={intercomUrl} target="_blank" rel="noreferrer">
                Intercom Profile
              </a>
            </div>
          </div>
        </div>

        <div className="responsive-columns">
          <div className="col-6 px6">
            <UserStatus userId={userId} />
            <UserConnection userId={userId} />
          </div>

          <div className="col-6 px6">
            <Device userId={userId} kind="Camera" />
            <Device userId={userId} kind="Microphone" />
            <Device userId={userId} kind="Audio Output" />
            <UserComputer userId={userId} />
          </div>
        </div>
      </div>
      <LocalModal />
      <MonitoringPubsubMessageObserver />
    </div>
  );
}

export { MonitoringClassUserDashboard };
